import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Types
export interface TabBarProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  variant?: "tab-bar" | "tab-bar-text";
  orientation?: "horizontal" | "vertical";
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
}

export interface TabBarItemProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  value: string;
  label: string;
  count?: number | string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface TabBarContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  value: string;
  "data-state"?: "active" | "inactive";
}

export interface TabBarControlProps
  extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

// Context
const TabBarContext = React.createContext<{
  value: string;
  onValueChange: (value: string) => void;
  variant: "tab-bar" | "tab-bar-text";
  name: string;
} | null>(null);

// Hook
const useTabBar = () => {
  const context = React.useContext(TabBarContext);
  if (!context) {
    throw new Error("TabBar components must be used within a TabBar");
  }
  return context;
};

// Main Component
const TabBar = React.forwardRef<HTMLDivElement, TabBarProps>(
  (
    {
      className,
      asChild = false,
      variant = "tab-bar",
      orientation = "horizontal",
      value,
      onValueChange,
      defaultValue,
      children,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || ""
    );
    const controlledValue = value !== undefined ? value : internalValue;
    const handleValueChange = (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    // Ensure we have a valid initial value
    React.useEffect(() => {
      if (
        defaultValue !== undefined &&
        defaultValue !== "" &&
        internalValue === ""
      ) {
        setInternalValue(defaultValue);
      }
    }, [defaultValue, internalValue]);

    const Comp = asChild ? Slot : "div";

    // Generate a unique name for radio button grouping
    const tabBarName = React.useMemo(
      () => `tab-bar-${Math.random().toString(36).substr(2, 9)}`,
      []
    );

    return (
      <TabBarContext.Provider
        value={{
          value: controlledValue,
          onValueChange: handleValueChange,
          variant,
          name: tabBarName,
        }}
      >
        <Comp
          ref={ref}
          data-slot="tab-bar"
          role="tablist"
          aria-orientation={orientation}
          className={cn(
            "wl-tabs-control",
            variant === "tab-bar" && "wl-tabs-control-bar",
            variant === "tab-bar-text" && "wl-tabs-control-text",
            className
          )}
          {...props}
        >
          {children}
        </Comp>
      </TabBarContext.Provider>
    );
  }
);

// Tab Item Component
const TabBarItem = React.forwardRef<HTMLDivElement, TabBarItemProps>(
  (
    {
      className,
      asChild = false,
      value,
      label,
      count,
      disabled = false,
      icon,
      children,
      ...props
    },
    ref
  ) => {
    const { value: selectedValue, onValueChange, variant, name } = useTabBar();
    const isSelected = selectedValue === value;
    const isDisabled = disabled;

    const handleClick = () => {
      if (!isDisabled) {
        onValueChange(value);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    };

    const Comp = asChild ? Slot : "div";

    if (variant === "tab-bar-text") {
      return (
        <Comp
          ref={ref}
          data-slot="tab-bar-item"
          role="tab"
          aria-selected={isSelected}
          aria-disabled={isDisabled}
          className={cn(
            "wl-tabs-text",
            isSelected && "wl-tabs-text-selected",
            isDisabled && "wl-tabs-text-disabled",
            className
          )}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={isDisabled ? -1 : 0}
          {...props}
        >
          <input
            id={`${name}-${value}`}
            aria-label={label}
            type="radio"
            name={name}
            checked={isSelected}
            disabled={isDisabled}
            readOnly
            className="wl-input-radio"
          />
          <label
            htmlFor={`${name}-${value}`}
            className="wl-d-inline-flex wl-align-items-center wl-gap-1"
          >
            {icon && <span className="wl-tabs-icon">{icon}</span>}
            {label}
          </label>
        </Comp>
      );
    }

    // Default tab-bar variant
    return (
      <Comp
        ref={ref}
        data-slot="tab-bar-item"
        role="tab"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        className={cn(
          "wl-tabs-bar",
          isSelected && "wl-tabs-bar-selected",
          isDisabled && "wl-tabs-bar-disabled",
          className
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        {...props}
      >
        <input
          id={`${name}-${value}`}
          aria-label={label}
          type="radio"
          name={name}
          checked={isSelected}
          disabled={isDisabled}
          readOnly
          className="wl-input-radio"
        />
        <label
          htmlFor={`${name}-${value}`}
          className="wl-d-inline-flex wl-align-items-center wl-gap-1"
        >
          {icon && <span className="wl-tabs-icon">{icon}</span>}
          {label}
          {count !== undefined && (
            <span className="wl-tabs-count">{count}</span>
          )}
        </label>
        <div className="wl-w-100">
          <div
            className={cn("wl-tabs-line", isSelected && "wl-tabs-line-visible")}
          />
        </div>
      </Comp>
    );
  }
);

// Tab Content Component
const TabBarContent = React.forwardRef<HTMLDivElement, TabBarContentProps>(
  (
    {
      className,
      asChild = false,
      value,
      "data-state": dataState,
      children,
      ...props
    },
    ref
  ) => {
    const { value: selectedValue } = useTabBar();
    const isActive = selectedValue === value;

    if (!isActive) {
      return null;
    }

    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        data-slot="tab-bar-content"
        role="tabpanel"
        data-state={isActive ? "active" : "inactive"}
        className={cn("wl-tabs-content", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

// Tab Control Component (for grouping multiple tabs)
const TabBarControl = React.forwardRef<HTMLDivElement, TabBarControlProps>(
  ({ className, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        data-slot="tab-bar-control"
        className={cn("wl-tabs-list", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

// Hook for managing tab bar state
export const useTabBarState = (defaultValue?: string) => {
  const [value, setValue] = React.useState(defaultValue || "");

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
  };

  return {
    value,
    onValueChange: handleValueChange,
  };
};

TabBar.displayName = "TabBar";
TabBarItem.displayName = "TabBarItem";
TabBarContent.displayName = "TabBarContent";
TabBarControl.displayName = "TabBarControl";

export { TabBar, TabBarItem, TabBarContent, TabBarControl, useTabBar };
